import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import Notification from '@components/ui/Notification'
import currencyConverter from '@helpers/misc/currencyConverter'
import ProgressBar from '@components/ui/ProgressBar'
import { checkStoreIs } from '@helpers/env'
import { ReactComponent as SunglassesIcon } from '@svgs/keyline/sunglasses.svg'
import Translations from '@helpers/translations'
import styles from '../styles.module.scss'
import { hasFreeShippingExperiment } from '../CartSideDrawer'

type StoreShippingFeesThreshold = {
  value: number
  cost: string
}

type CartNotificationText = {
  showCartNotification: boolean
  storeShippingFeesThreshold: StoreShippingFeesThreshold
}

type CartNotificationProps = {
  showUsNotification: boolean
  storeShippingFeesThreshold: StoreShippingFeesThreshold
  showFreeShippingMethod: boolean
  totalPrice: number
  isCartEmpty: boolean
}

const getCartNotificationText = ({
  showCartNotification,
  storeShippingFeesThreshold
}: CartNotificationText) => {
  if (showCartNotification) {
    return Translations.localise('common.cart.invalid-items-removed')
  }

  return `${Translations.localise('common.sweetenersList.option-1')} ${
    storeShippingFeesThreshold.cost
  }`
}

type ShippingMessageProps = {
  showFreeShippingMethod: boolean
  remainderToFreeDelivery: number
}

const geProgressBarShippingMessage = ({
  showFreeShippingMethod,
  remainderToFreeDelivery
}: ShippingMessageProps) => {
  const remainingFreeThresholdValue = currencyConverter({
    price: remainderToFreeDelivery
  })
  return showFreeShippingMethod ? (
    <>
      <span>Happy days. You get free delivery.</span>
      <SunglassesIcon />
    </>
  ) : (
    <span>
      You’re
      <strong> {remainingFreeThresholdValue} </strong>
      away from free delivery
    </span>
  )
}

type CartNotificationState = {
  cartNotification: {
    showCartNotification: boolean
  }
}

const CartNotification = ({
  showUsNotification,
  showFreeShippingMethod,
  storeShippingFeesThreshold,
  totalPrice,
  isCartEmpty
}: CartNotificationProps) => {
  const { showCartNotification } = useSelector(
    (state: CartNotificationState) => state.cartNotification
  )
  const [shouldStartProgressBar, setShouldStartProgressBar] = useState(false)

  useEffect(() => {
    const progressBarTimeout = setTimeout(() => {
      setShouldStartProgressBar(true)
    }, 500)

    return () => {
      clearTimeout(progressBarTimeout)
    }
  }, [])

  const freeShippingOverride = hasFreeShippingExperiment()

  const shippingThresholdProgress = shouldStartProgressBar
    ? totalPrice / storeShippingFeesThreshold.value
    : 0

  const freeShippingProgress = freeShippingOverride
    ? 1
    : shippingThresholdProgress

  const showEuNotification =
    checkStoreIs('eu') && totalPrice > storeShippingFeesThreshold.value

  if (showUsNotification) {
    return (
      <Notification
        theme="dark"
        type="success"
        content={
          <div className={styles.usNotification}>
            <strong>UK to US tariffs: we’ve got you covered</strong>
            <br /> We’re taking care of all US import duties, so the price you
            see at checkout is the price you’ll pay. No hidden fees. No nasty
            surprises.
          </div>
        }
      />
    )
  }

  if (showEuNotification) {
    return (
      <Notification theme="dark" type="error">
        <p>
          {totalPrice > 250
            ? 'Free shipping on orders under 150€. Orders over 250€ will be subject to a 35€ delivery fee to cover EU custom duties.'
            : Translations.localise('common.cart.notifications.text-message')}
          <br />
          <a
            href="https://help.spoke-london.com/hc/en-us/articles/360014405178-What-are-your-delivery-options-"
            target="_blank"
          >
            {Translations.localise('common.cart.notifications.text-link')}
          </a>
        </p>
      </Notification>
    )
  }

  const progressBarShippingMessage = geProgressBarShippingMessage({
    showFreeShippingMethod,
    remainderToFreeDelivery: freeShippingOverride
      ? 0
      : storeShippingFeesThreshold.value - totalPrice
  })

  const defaultCartShippingMessage = getCartNotificationText({
    showCartNotification,
    storeShippingFeesThreshold
  })

  const shouldShowProgressBarNotification =
    !showCartNotification && !checkStoreIs('eu')

  return (
    <div className={styles.CartNotification}>
      {shouldShowProgressBarNotification ? (
        <div className={styles.CartNotification__progressBarWrapper}>
          <p className={styles.cart__note}>
            {isCartEmpty
              ? defaultCartShippingMessage
              : progressBarShippingMessage}
          </p>
          <ProgressBar
            theme="secondary"
            progress={freeShippingProgress < 1 ? freeShippingProgress : 1}
          />
        </div>
      ) : (
        <Notification variant="info">{defaultCartShippingMessage}</Notification>
      )}
    </div>
  )
}

export default CartNotification
